import React, { useState } from "react";
import { useOfferLetters } from "../../contexts/OfferLettersContext";
import Button from "../../components/UI/Button";
import Card from "../../components/UI/Card";
import Modal from "../../components/UI/Modal";
import CandidateSelector from "../Candidates/CandidateSelector";
import OfferTemplateSelector from "../OfferTemplates/OfferTemplateSelector";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";

const OfferLettersPage = () => {
    const { offerLetters, loading, error, createOfferLetter, updateOfferLetter, deleteOfferLetter } = useOfferLetters();
    const [selectedOfferLetter, setSelectedOfferLetter] = useState(null); // new state for editing
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        candidate_id: "",
        template_id: "",
        position: "",
        joining_date: "",
    });
    const [notification, setNotification] = useState(null);
    const [formErrors, setFormErrors] = useState({}); // Validation errors
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateOfferLetter = async (e) => {
        e.preventDefault();
        try {
            await createOfferLetter(formData);
            setShowModal(false);
            setFormData({
                candidate_id: "",
                template_id: "",
                position: "",
                joining_date: "",
            });
        } catch (err) {
            console.error("Error creating offer letter:", err);
            alert("Failed to create offer letter.");
            // setNotification("Failed to create offer letter.");
            setNotification({
                message: "Failed to create offer letter.",
                type: "error",
            });
        }
    };

     // Open modal for editing an offer letter
    const handleOpenEditModal = (offer) => {
        setSelectedOfferLetter(offer);
        setFormData({
            candidate_id: offer.candidate_id,
            template_id: offer.template_id,
            position: offer.position,
            joining_date: offer.joining_date,
        });
        setShowModal(true);
    };

    
    const validate = () => {
        const validationErrors = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const onlyTextRegex = /^[A-Za-z\s]+$/;


        if (!formData.position.trim()) {
            validationErrors.position = "Position is required.";
        } else if (!onlyTextRegex.test(formData.position.trim())) {
            validationErrors.position = "Position should contain only letters and spaces.";
        }

        if (!formData.joining_date) {
            validationErrors.joining_date = "Joining date is required.";
        } else {
            const selectedDate = new Date(formData.joining_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // normalize to compare only date
        
            if (selectedDate < today) {
                validationErrors.joining_date = "Joining date must be today or a future date.";
            }
        }

        return validationErrors;
    };
    

    const handleSubmit = async (e) => {
        e.preventDefault();
              
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
        setFormErrors(validationErrors);
        return;
        }	
        
        try {
            if (selectedOfferLetter) {
                // If editing, update the offer letter
                await updateOfferLetter(selectedOfferLetter.id, formData);
                // setNotification("Offer letter updated successfully!");
                setNotification({
                    message: "Offer letter updated successfully!",
                    type: "success",
                });
                setFormErrors("")
            } else {
                // If creating, create a new offer letter
                await createOfferLetter(formData);
                // setNotification("Offer letter created successfully!");
                setNotification({
                    message: "Offer letter created successfully!",
                    type: "success",
                });
                setFormErrors("")
            }
            setShowModal(false);
        } catch (err) {
            console.error("Error processing offer letter:", err);
            alert("Failed to process offer letter.");
            // setNotification("Failed to process offer letter.");
            setNotification({
                message: "Failed to process offer letter.",
                type: "error",
            });
        }
    };

    const handleDeleteOfferLetter = async (id) => {
        try {
          await deleteOfferLetter(id);
        //   setNotification("Offer letter deleted successfully!");
          setNotification({
            message: "Offer letter deleted successfully!",
            type: "success",
        });
        } catch (error) {
          console.error("Error deleting offer letter:", error);
        //   setNotification("Failed to delete offer letter.");
            setNotification({
                message: "Failed to delete offer letter.",
                type: "error",
            });
        }
    };
    

    return (
        <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
            
            {/* notification compo */}
            {notification && (
                <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
            )}

            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-white">Offer Letters</h1>
                    <Button
                        onClick={() => {
                            setSelectedOfferLetter(null); // Reset selected offer letter
                            setFormData({ // Reset form fields
                                candidate_id: "",
                                template_id: "",
                                position: "",
                                joining_date: "",
                            });
                            setShowModal(true);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
                    >
                        Add Offer Letter
                    </Button>
                </div>

                {/* Offer Letters List */}
                <div className="space-y-4">
                    {loading ? (
                        <p className="text-center dark:text-white">Loading...</p>
                    ) : error ? (
                        <p className="text-red-500 text-center">{error}</p>
                    ) : offerLetters.length === 0 ? (
                        <p className="text-center dark:text-white">No offer letters available.</p>
                    ) : (
                        offerLetters.map((offer) => (
                            <Card key={offer.id}>
                                <h2 className="md:text-xl text-md font-semibold">{offer.position}</h2>
                                <p>Candidate: {offer.candidate.name}</p>
                                <p>Joining Date: {offer.joining_date}</p>
                                <p>Status: {offer.status}</p>
                                <div className="flex gap-2 mt-2">
                                    <Button onClick={() => handleOpenEditModal(offer)} className="bg-blue-600 hover:bg-blue-700">
                                        Edit
                                    </Button>
                                    {/* old code */}
                                    {/* <Button onClick={() => deleteOfferLetter(offer.id)} className="bg-red-600 hover:bg-red-700">
                                        Delete
                                    </Button> */}
                                    {/* new component */}
                                    <DeleteButton style={`px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700`} onDelete={() => handleDeleteOfferLetter(offer.id)} />
                                </div>
                            </Card>
                        ))
                    )}
                </div>

                {/* new modal by combining create and update offer letter */}
                {/* Modal for Create/Edit Offer Letter */}
                <Modal isOpen={showModal} onRequestClose={() => setShowModal(false)} title={selectedOfferLetter ? "Edit Offer Letter" : "Add Offer Letter"}>
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-white">Candidate Name: <span className="text-red-500 ml-1">*</span></label>
                            <CandidateSelector formData={formData} setFormData={setFormData} />
                            {formErrors.candidate_id && <p className="text-red-500 text-sm mt-1">{formErrors.candidate_id}</p>}
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-white">Template Name: <span className="text-red-500 ml-1">*</span></label>
                            <OfferTemplateSelector formData={formData} setFormData={setFormData} />
                            {formErrors.template_id && <p className="text-red-500 text-sm mt-1">{formErrors.template_id}</p>}
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-white">Position: <span className="text-red-500 ml-1">*</span></label>
                            <input type="text" name="position" value={formData.position} onChange={handleInputChange} className="w-full p-2 dark:text-white dark:bg-black dark:border-gray-700 dark:focus:outline-none border border-gray-300 rounded" required />
                            {formErrors.position && <p className="text-red-500 text-sm mt-1">{formErrors.position}</p>}
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-white">Joining Date: <span className="text-red-500 ml-1">*</span></label>
                            <input type="date" name="joining_date" value={formData.joining_date} onChange={handleInputChange} className="w-full p-2 dark:text-white dark:bg-black dark:border-gray-700 dark:focus:outline-none border border-gray-300 rounded" required />
                            {formErrors.joining_date && <p className="text-red-500 text-sm mt-1">{formErrors.joining_date}</p>}
                        </div>
                        <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                            {selectedOfferLetter ? "Update Offer Letter" : "Submit"}
                        </Button>
                    </form>
                </Modal>
                
            </div>
        </div>
    );
};

export default OfferLettersPage;
